import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Typography,
  Box,
} from "@mui/material";

// —— Netflix-ish tokens (visual only)
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.75)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
  redHover: "#b20710",
};

interface PurchaseDialogProps {
  open: boolean;
  onClose: () => void;
  purchaseAmount: number;
  setPurchaseAmount: (v: number) => void;
  onConfirm: () => void;
}

const PurchaseDialog: React.FC<PurchaseDialogProps> = ({
  open,
  onClose,
  purchaseAmount,
  setPurchaseAmount,
  onConfirm,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
          padding: "8px 0",
          fontFamily: "'Lexend', sans-serif",
          backgroundColor: COLORS.panel,
          border: `1px solid ${COLORS.border}`,
          color: COLORS.text,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: "1.25rem",
          textAlign: "center",
          color: COLORS.text,
        }}
      >
        Purchase Chips
      </DialogTitle>

      <DialogContent sx={{ px: 4, py: 2 }}>
        <Typography
          variant="body2"
          sx={{ mb: 1, color: COLORS.muted, textAlign: "center" }}
        >
          Enter the amount you want to add to your wallet
        </Typography>
        <TextField
          fullWidth
          label="Amount"
          type="number"
          value={purchaseAmount}
          onChange={(e) => setPurchaseAmount(Number(e.target.value))}
          InputProps={{
            inputProps: { min: 1 },
          }}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: "12px",
              backgroundColor: "rgba(255,255,255,0.06)",
              color: COLORS.text,
              "& fieldset": {
                borderColor: COLORS.border,
              },
              "&:hover fieldset": {
                borderColor: "rgba(255,255,255,0.2)",
              },
              "&.Mui-focused fieldset": {
                borderColor: COLORS.red,
              },
            },
            "& .MuiInputLabel-root": {
              fontFamily: "'Lexend', sans-serif",
              color: COLORS.muted,
            },
            "& .MuiInputBase-input": {
              color: COLORS.text,
            },
          }}
        />
        <Box mt={2} textAlign="center">
          <Typography variant="caption" sx={{ color: COLORS.muted }}>
            Payment will be processed via Stripe Checkout
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 3,
          pb: 3,
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            fontWeight: 700,
            textTransform: "none",
            color: COLORS.muted,
            px: 4,
            py: 1,
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          sx={{
            background: COLORS.red,
            color: COLORS.text,
            fontWeight: 700,
            textTransform: "none",
            px: 4,
            py: 1,
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(229,9,20,0.3)",
            transition: "all 0.3s ease",
            "&:hover": {
              background: COLORS.redHover,
              boxShadow: "0 6px 14px rgba(229,9,20,0.4)",
            },
          }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseDialog;
